/**
 * Create Your Own — bodice × skirt sparse matrix.
 * mode "page": standalone page picker + View dress link.
 * mode "matrix": product template picker; AJAX-swaps [data-main-product-section] via ProductFamilyAjax.
 */
(function () {
  if (window.CyoConfigurator) return;

  function handleize(str) {
    return String(str || '')
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .replace(/-+/g, '-');
  }

  function stripHtml(value) {
    return String(value || '').replace(/<[^>]*>/g, '').trim();
  }

  function pathOf(url) {
    try {
      return new URL(url, window.location.origin).pathname;
    } catch (e) {
      return String(url || '').split('?')[0];
    }
  }

  function isColorAxis(axis) {
    if (!axis) return false;
    const type = String(axis.type || '').toLowerCase();
    const key = String(axis.key || '').toLowerCase();
    return type === 'color' || key === 'color';
  }

  function structureAxes(axes) {
    return (axes || []).filter((axis) => !isColorAxis(axis));
  }

  class CyoConfigurator {
    constructor(root) {
      this.root = root;
      root.cyoInstance = this;
      this.bootstrapEl = root.querySelector('[data-cyo-bootstrap]');
      if (!this.bootstrapEl) return;

      let data;
      try {
        data = JSON.parse(this.bootstrapEl.textContent);
      } catch (err) {
        console.warn('[CYO] Invalid bootstrap JSON', err);
        return;
      }

      this.mode = data.mode || root.getAttribute('data-cyo-mode') || 'page';
      this.axes = data.axes || [];
      this.routes = data.routes || {};
      if (this.routes._cyo_invalid) {
        root.querySelectorAll('input, button').forEach(input => { input.disabled = true; });
        const status = root.querySelector('[data-cyo-status]');
        if (status) status.textContent = 'Combination selection is temporarily unavailable.';
        return;
      }
      this.defaultSelection = data.defaultSelection || {};
      this.currentSelection = data.currentSelection || {};
      this.currentProductUrl = data.currentProductUrl || window.location.pathname;
      this.seriesTitle = data.seriesTitle || '';
      this.structureKeys = structureAxes(this.axes).map((a) => a.key);
      this.colorAxis = this.axes.find((a) => isColorAxis(a)) || null;
      this._navigating = false;

      this.els = {
        image: root.querySelector('[data-cyo-image]'),
        summary: root.querySelector('[data-cyo-summary]'),
        price: root.querySelector('[data-cyo-price]'),
        cta: root.querySelector('[data-cyo-cta]'),
        hint: root.querySelector('[data-cyo-hint]'),
        colorOptions: root.querySelector('[data-cyo-color-options]'),
      };

      this.selection = this.buildInitialSelection();
      this.bindEvents();
      this.render({ skipNavigate: true });
    }

    isMatrixMode() {
      return this.mode === 'matrix' || this.mode === 'shopping';
    }

    buildInitialSelection() {
      const selection = {};
      const routeKeys = Object.keys(this.routes);
      const seed = this.isMatrixMode() ? this.currentSelection : this.defaultSelection;

      this.structureKeys.forEach((key) => {
        if (seed[key]) selection[key] = seed[key];
        else if (this.defaultSelection[key]) selection[key] = this.defaultSelection[key];
      });

      let route = this.findRoute(selection);
      if (!route && routeKeys.length && !this.isMatrixMode()) {
        route = this.nearestRoute(selection) || this.routes[routeKeys[0]];
        if (route && route.attrs) {
          this.structureKeys.forEach((key) => {
            if (route.attrs[key] != null) selection[key] = route.attrs[key];
          });
        }
      }

      if (this.colorAxis) {
        const colors = (route && route.colors) || [];
        const preferred =
          seed.color ||
          seed[this.colorAxis.key] ||
          this.defaultSelection.color ||
          this.defaultSelection[this.colorAxis.key];
        const match = colors.find((c) => c.name === preferred) || colors[0];
        selection[this.colorAxis.key] = match ? match.name : '';
      }

      return selection;
    }

    bindEvents() {
      this.root.addEventListener('change', (event) => {
        const colorSelect = event.target.closest('[data-cyo-color-select]');
        if (colorSelect && this.colorAxis) {
          this.selection[this.colorAxis.key] = colorSelect.value;
          this.render();
          return;
        }
        const input = event.target.closest('[data-cyo-option]');
        if (!input || !this.root.contains(input)) return;
        const key = input.getAttribute('data-axis-key');
        if (!key) return;
        const previous = { ...this.selection };
        this.selection[key] = input.value;
        this.reconcileAfterStructureChange(key);
        const adjusted = this.structureKeys.some((other) => other !== key && previous[other] !== this.selection[other]);
        const colorChanged = this.colorAxis && previous[this.colorAxis.key] !== this.selection[this.colorAxis.key];
        const notice = adjusted ? 'The skirt has been adjusted to an available combination.' : '';
        const colorNotice = colorChanged ? 'The previous color is unavailable; a new color has been selected.' : '';
        this.notice = [notice, colorNotice].filter(Boolean).join(' ');
        this.render();
      });

      this.root.addEventListener('click', (event) => {
        const swatch = event.target.closest('[data-cyo-color-swatch]');
        if (!swatch || !this.root.contains(swatch)) return;
        event.preventDefault();
        if (swatch.hasAttribute('disabled') || swatch.getAttribute('aria-disabled') === 'true') return;
        const key = this.colorAxis ? this.colorAxis.key : 'color';
        this.selection[key] = swatch.getAttribute('data-color-name') || '';
        this.render();
      });


    }

    routeKeyFromSelection(selection) {
      const parts = this.structureKeys.map((key) => handleize(selection[key]));
      if (parts.some((p) => !p)) return '';
      return parts.join('|');
    }

    findRoute(selection) {
      const key = this.routeKeyFromSelection(selection);
      if (!key) return null;
      return this.routes[key] || null;
    }

    reachableValues(axisKey, selection) {
      const values = new Set();
      Object.keys(this.routes).forEach((routeKey) => {
        const route = this.routes[routeKey];
        if (!route || !route.attrs) return;
        const ok = this.structureKeys.every((key) => {
          if (key === axisKey) return true;
          if (!selection[key]) return true;
          return handleize(route.attrs[key]) === handleize(selection[key]);
        });
        if (ok && route.attrs[axisKey] != null) {
          values.add(String(route.attrs[axisKey]));
        }
      });
      return values;
    }

    reconcileAfterStructureChange(changedKey) {
      this.structureKeys.forEach((key) => {
        if (key === changedKey) return;
        const reachable = this.reachableValues(key, this.selection);
        if (!reachable.has(String(this.selection[key] || ''))) {
          const next = reachable.values().next().value;
          if (next != null) this.selection[key] = next;
        }
      });

      let route = this.findRoute(this.selection);
      if (!route) {
        route = this.nearestRoute(this.selection);
        if (route && route.attrs) {
          this.structureKeys.forEach((key) => {
            if (route.attrs[key] != null) this.selection[key] = route.attrs[key];
          });
        }
      }

      if (this.colorAxis && route) {
        const colors = route.colors || [];
        const colorKey = this.colorAxis.key;
        const current = this.selection[colorKey];
        const still = colors.find((c) => c.name === current);
        if (!still) {
          this.selection[colorKey] = colors[0] ? colors[0].name : '';
        }
      }
    }

    nearestRoute(selection) {
      const keys = Object.keys(this.routes);
      let best = null;
      let bestScore = -1;
      keys.forEach((routeKey) => {
        const route = this.routes[routeKey];
        if (!route || !route.attrs) return;
        let score = 0;
        this.structureKeys.forEach((key) => {
          if (selection[key] && handleize(route.attrs[key]) === handleize(selection[key])) {
            score += 1;
          }
        });
        if (score > bestScore) {
          bestScore = score;
          best = route;
        }
      });
      return best;
    }

    resolveColor(route) {
      if (!route) return null;
      const colors = route.colors || [];
      if (!colors.length) {
        return {
          name: '',
          url: route.url,
          image: route.image,
        };
      }
      const colorKey = this.colorAxis ? this.colorAxis.key : 'color';
      const name = this.selection[colorKey];
      return colors.find((c) => c.name === name) || colors[0];
    }

    updateOptionDisabledState() {
      this.root.querySelectorAll('[data-cyo-option]').forEach((input) => {
        const key = input.getAttribute('data-axis-key');
        if (!key || !this.structureKeys.includes(key)) return;
        const reachable = this.reachableValues(key, key === this.structureKeys[0] ? {} : this.selection);
        const allowed = reachable.has(input.value);
        input.disabled = !allowed;
        const label = input.closest('.cyo-option');
        if (label) label.classList.toggle('is-disabled', !allowed);
        if (String(this.selection[key]) === String(input.value)) {
          input.checked = true;
        }
      });
    }

    renderColorSwatches(route) {
      const host = this.els.colorOptions;
      if (!host || !this.colorAxis) return;

      const colors = (route && route.colors) || [];
      const colorKey = this.colorAxis.key;
      const selected = this.selection[colorKey];
      const select = this.root.querySelector('[data-cyo-color-select]');
      if (select) {
        select.innerHTML = colors.map(color => `<option value="${escapeAttr(color.name)}"${color.name === selected ? ' selected' : ''}>${escapeHtml(color.name)}</option>`).join('');
        const chip = this.root.querySelector('[data-cyo-selected-color]');
        const current = colors.find(color => color.name === selected);
        if (chip) {
          chip.style.removeProperty('background-image');
          chip.className = `cyo-color-select__chip value-color bg-color-${escapeAttr(current?.handle || handleize(selected))}`;
          chip.dataset.cyoSwatchImage = current?.image || '';
        }
      }

      host.innerHTML = colors
        .map((color) => {
          const isSelected = color.name === selected;
          const handle = color.handle || handleize(color.name);
          return (
            `<button type="button" class="cyo-color-swatch${isSelected ? ' is-selected' : ''}"` +
            ` data-cyo-color-swatch data-color-name="${escapeAttr(color.name)}" data-color-handle="${escapeAttr(handle)}"` +
            ` title="${escapeAttr(color.name)}" aria-pressed="${isSelected ? 'true' : 'false'}">` +
            `<span class="cyo-color-swatch__chip value-color bg-color-${escapeAttr(handle)}" data-cyo-swatch-image="${escapeAttr(color.image || '')}" aria-hidden="true"></span>` +
            `<span class="visually-hidden">${escapeHtml(color.name)}</span>` +
            `</button>`
          );
        })
        .join('');
      if (typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(() => this.applySwatchFallbacks());
      }
    }

    applySwatchFallbacks() {
      if (!this.root.isConnected) return;
      this.root.querySelectorAll('[data-cyo-swatch-image]').forEach(chip => {
        const style = window.getComputedStyle(chip);
        const transparent = style.backgroundColor === 'transparent' || /(?:rgba\([^)]*,\s*0(?:\.0+)?\)|\/\s*0(?:\.0+)?\s*\))$/.test(style.backgroundColor);
        if (!transparent || (style.backgroundImage && style.backgroundImage !== 'none')) return;
        const image = chip.dataset.cyoSwatchImage;
        if (image) {
          try {
            const url = new URL(image, window.location.origin);
            if (url.protocol !== 'https:' && url.protocol !== 'http:') return;
            chip.style.backgroundImage = `url(${JSON.stringify(url.href)})`;
            chip.style.backgroundPosition = 'center 25%';
            chip.style.backgroundSize = 'cover';
          } catch (error) { /* Keep an explicit unknown-preview marker below. */ }
        }
        if (!chip.style.backgroundImage) chip.classList.add('cyo-swatch-unavailable');
      });
    }

    labelForSelection(key, rawValue) {
      if (!rawValue) return '';
      const input = this.root.querySelector(
        `[data-cyo-option][data-axis-key="${CSS.escape(key)}"][value="${CSS.escape(String(rawValue))}"]`
      );
      if (input) {
        return input.getAttribute('data-option-label') || rawValue;
      }
      return rawValue;
    }

    targetUrl(route, color) {
      if (color && color.url) return color.url;
      if (route && route.url) return route.url;
      return '';
    }

    navigateToSelection(url) {
      if (!url) return;
      const nextPath = pathOf(url);
      const currentPath = pathOf(window.location.href);
      if (this.mode !== 'shopping' && nextPath === currentPath) return;

      this._navigating = true;
      this.root.dispatchEvent(new CustomEvent('cyo:selection', {
        bubbles: true, detail: { url, selection: { ...this.selection }, notice: this.notice || '' }
      }));
      this.notice = '';
    }

    syncFromLocation() {
      if (!this.isMatrixMode() || !this.root.isConnected) return;
      const path = window.location.pathname;
      const keys = Object.keys(this.routes);
      for (let i = 0; i < keys.length; i += 1) {
        const route = this.routes[keys[i]];
        if (!route) continue;
        const colors = route.colors || [];
        let match = colors.find((c) => pathOf(c.url) === path);
        if (!match && pathOf(route.url) === path) {
          match = colors[0] || { name: '', url: route.url };
        }
        if (!match) continue;
        if (route.attrs) {
          this.structureKeys.forEach((key) => {
            if (route.attrs[key] != null) this.selection[key] = route.attrs[key];
          });
        }
        if (this.colorAxis && match.name) {
          this.selection[this.colorAxis.key] = match.name;
        }
        this.render({ skipNavigate: true });
        return;
      }
    }

    render(options = {}) {
      const skipNavigate = Boolean(options.skipNavigate);
      this.updateOptionDisabledState();

      const route = this.findRoute(this.selection);
      const color = this.resolveColor(route);
      const available = Boolean(route && color && color.url);
      const url = this.targetUrl(route, color);

      this.root.querySelectorAll('[data-cyo-axis]').forEach((fieldset) => {
        const key = fieldset.getAttribute('data-axis-key');
        const valueEl = fieldset.querySelector('[data-cyo-axis-value]');
        if (!valueEl) return;
        if (this.colorAxis && key === this.colorAxis.key) {
          valueEl.textContent = this.selection[key] || '';
        } else {
          valueEl.textContent = this.labelForSelection(key, this.selection[key]);
        }
      });

      this.renderColorSwatches(route);

      const dressName = (route && (route.spu || route.title)) || '';
      const summaryParts = [];
      if (dressName) summaryParts.push(dressName);
      this.structureKeys.forEach((key) => {
        const label = this.labelForSelection(key, this.selection[key]);
        if (label) summaryParts.push(label);
      });
      if (this.colorAxis && this.selection[this.colorAxis.key]) {
        summaryParts.push(this.selection[this.colorAxis.key]);
      }
      if (this.els.summary) {
        this.els.summary.textContent = summaryParts.join(' · ');
      }

      if (this.els.price) {
        this.els.price.textContent = route && route.price ? stripHtml(route.price) : '';
      }

      const imageUrl = (color && color.image) || (route && route.image) || '';
      if (this.els.image && imageUrl) {
        this.els.image.src = imageUrl;
        this.els.image.alt = summaryParts.join(' · ') || this.seriesTitle;
      }

      if (this.els.cta) {
        if (available) {
          this.els.cta.href = url;
          this.els.cta.removeAttribute('aria-disabled');
          this.els.cta.classList.remove('is-disabled');
        } else {
          this.els.cta.href = '#';
          this.els.cta.setAttribute('aria-disabled', 'true');
          this.els.cta.classList.add('is-disabled');
        }
      }

      if (this.els.hint) {
        this.els.hint.hidden = available;
      }

      if (this.isMatrixMode() && !skipNavigate && available && url) {
        this.navigateToSelection(url);
      }
    }

    static initAll(root = document) {
      root.querySelectorAll('[data-cyo-configurator]:not([data-cyo-ready])').forEach((el) => {
        if (el.hasAttribute('data-cyo-missing-series')) return;
        el.setAttribute('data-cyo-ready', 'true');
        new CyoConfigurator(el);
      });
    }
  }

  function escapeAttr(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  window.CyoConfigurator = CyoConfigurator;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => CyoConfigurator.initAll());
  } else {
    CyoConfigurator.initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    CyoConfigurator.initAll(event.target);
  });

})();
